import React, { useState } from 'react';
import { HighlightableText } from './HighlightableText';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { VerseReference } from '../types';

interface BoxDiagramProps {
  mainVerse: string;
  mainContent: string;
  references: VerseReference[];
  noteId: string;
  onToggleVerse: (noteId: string, verseId: string) => void;
  onAddReference: (noteId: string, parentId: string) => void;
  onEditReference: (noteId: string, reference: VerseReference) => void;
  onDeleteReference: (noteId: string, parentId: string | null, referenceId: string) => void;
}

interface BoxProps {
  verse: VerseReference;
  isMain?: boolean;
  level: number;
  noteId: string;
  parentId: string | null;
  onToggleVerse: (noteId: string, verseId: string) => void;
  onAddReference: (noteId: string, parentId: string) => void;
  onEditReference: (noteId: string, reference: VerseReference) => void;
  onDeleteReference: (noteId: string, parentId: string | null, referenceId: string) => void;
}

const ReferenceBox: React.FC<BoxProps> = ({
  verse,
  isMain = false,
  level,
  noteId,
  parentId,
  onToggleVerse,
  onAddReference,
  onEditReference,
  onDeleteReference
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const hasChildren = verse.linkedVerses && verse.linkedVerses.length > 0;

  return (
    <div className="relative">
      <div
        className={`relative border-2 rounded-lg p-3 sm:p-4 bg-white transition-all cursor-pointer ${
          isMain 
            ? 'border-blue-600 shadow-lg w-full' 
            : 'border-gray-300 hover:border-blue-400 shadow-md w-full'
        }`}
        onClick={() => hasChildren && onToggleVerse(noteId, verse.id)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex justify-between items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className={`font-bold ${isMain ? 'text-base sm:text-lg text-blue-700' : 'text-sm sm:text-base text-blue-600'} mb-1 break-words`}>
              {verse.display}
            </div>
            {verse.verseText && (
              <HighlightableText
                text={verse.verseText}
                highlights={verse.verseHighlights || []}
                onChange={() => {}}
                editable={false}
                className="text-xs sm:text-sm text-gray-700 leading-relaxed italic mb-2 whitespace-pre-wrap break-words"
              />
            )}
            {verse.content && (
              <div className="text-xs sm:text-sm text-gray-600 leading-relaxed border-t border-gray-200 pt-2 break-words">
                <span className="font-medium">Note: </span>{verse.content}
              </div>
            )}
          </div>
          
          {!isMain && isHovered && (
            <div className="flex gap-1 flex-shrink-0">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddReference(noteId, verse.id);
                }}
                className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                title="Add child reference"
              >
                <Plus size={14} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEditReference(noteId, verse);
                }}
                className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title="Edit"
              >
                <Edit2 size={14} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteReference(noteId, parentId, verse.id);
                }}
                className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>
          )}
        </div>

        {hasChildren && (
          <div className="mt-2 text-xs text-blue-600 font-medium">
            {verse.isExpanded ? '▼' : '▶'} {verse.linkedVerses!.length} reference{verse.linkedVerses!.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {verse.isExpanded && hasChildren && (
        <div className="mt-4 sm:mt-6 ml-4 sm:ml-8 space-y-4 relative">
          {/* Connecting line from parent */}
          <div className="hidden sm:block absolute left-[-32px] top-0 bottom-0 w-[2px] bg-gray-300"></div>
          
          {verse.linkedVerses!.map((childVerse, index) => (
            <div key={childVerse.id} className="relative">
              {/* Horizontal connecting line */}
              <div className="hidden sm:block absolute left-[-32px] top-[24px] w-[32px] h-[2px] bg-gray-300"></div>
              
              <ReferenceBox
                verse={childVerse}
                level={level + 1}
                noteId={noteId}
                parentId={verse.id}
                onToggleVerse={onToggleVerse}
                onAddReference={onAddReference}
                onEditReference={onEditReference}
                onDeleteReference={onDeleteReference}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const BoxDiagram: React.FC<BoxDiagramProps> = ({
  mainVerse,
  mainContent,
  references,
  noteId,
  onToggleVerse,
  onAddReference,
  onEditReference,
  onDeleteReference
}) => {
  const [showReferences, setShowReferences] = useState(true);

  return (
    <div className="py-2 sm:py-4">
      <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-8">
        {/* Main verse box */}
        <div className="w-full sm:w-64 sm:flex-shrink-0">
          <div 
            className="border-2 border-blue-600 rounded-lg p-3 sm:p-4 bg-white shadow-lg w-full cursor-pointer hover:shadow-xl transition-shadow"
            onClick={() => setShowReferences(!showReferences)}
          >
            <div className="font-bold text-base sm:text-lg text-blue-700 mb-1 break-words">
              {mainVerse}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 leading-relaxed break-words">
              {mainContent}
            </div>
            {references.length > 0 && (
              <div className="mt-2 text-xs text-blue-600 font-medium">
                {showReferences ? '▼' : '▶'} {references.length} reference{references.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>

        {/* References */}
        {references.length > 0 && showReferences && (
          <div className="w-full sm:flex-1 space-y-4 relative">
            {/* Connecting lines container */}
            <div className="hidden sm:block absolute left-[-32px] top-0 bottom-0 w-[2px] bg-gray-300"></div>
            
            {references.map((ref) => {
              const refWithDefaults = {
                ...ref,
                content: ref.content || '',
                linkedVerses: ref.linkedVerses || [],
                isExpanded: ref.isExpanded || false
              };
              
              return (
                <div key={ref.id} className="relative">
                  {/* Horizontal connecting line */}
                  <div className="hidden sm:block absolute left-[-32px] top-[24px] w-[32px] h-[2px] bg-gray-300"></div>
                  
                  <ReferenceBox
                    verse={refWithDefaults}
                    level={1}
                    noteId={noteId}
                    parentId={null}
                    onToggleVerse={onToggleVerse}
                    onAddReference={onAddReference}
                    onEditReference={onEditReference}
                    onDeleteReference={onDeleteReference}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
